import { map, pick, omit, trimStart, get } from 'lodash';
import { EXPERIMENT_USE_CUSTOM_ROUTER } from '@wix/communities-blog-experiments';
import Wix from './wix-sdk-polyfill';

import { push, replace } from './router-utils';
import { setQueryParams } from './sdk-query-params';
import { isInEditor, isInSite } from './view-mode';
import { getCurrentLocation } from '../selectors/location-history-selectors';
import { isExperimentEnabled } from '@wix/communities-blog-client-common/dist/src/selectors/experiments-selectors';
import { isOneApp } from '@wix/communities-blog-client-common/dist/src/selectors/search-params-selectors';
import { isErrorPage } from '@wix/communities-blog-client-common/dist/src/constants/errors';
import { readableUrl } from '@wix/communities-blog-client-common/dist/src/services/uri';

const ROUTING_PARAMS = ['sort', 'name', 'query', 'redirect', 'page'];
const ROOT_PATH = './';

const getSearch = (locationQuery = {}) => {
  const queryParams = map(
    pick(locationQuery, ROUTING_PARAMS),
    (value, name) => `${name}=${encodeURIComponent(value)}`,
  ).join('&');
  return queryParams ? `?${queryParams}` : '';
};

const getWixSectionPathname = (location = {}) => {
  const search = getSearch(location.query);
  // wix sdk requires path to be present and it has to contain path without '/'
  const path = readableUrl(trimStart(location.pathname, '/'));
  return `${path}${search}` || ROOT_PATH;
};

const replaceSectionState = (state, pathname) => {
  if (isExperimentEnabled(state, EXPERIMENT_USE_CUSTOM_ROUTER)) {
    return Wix.getComponentInfo(info => {
      const msg = {
        intent: 'TPA2',
        type: 'replaceSectionState',
        compId: info.compId,
        data: {
          ignorePageUriSeo: true,
          state: pathname,
        },
      };
      window.parent.postMessage(JSON.stringify(msg), '*');
    });
  }
  Wix.replaceSectionState(pathname);
};

const syncWixSectionState = state => {
  if (isInEditor()) {
    return;
  }

  const location = getCurrentLocation(state);
  if (location.pathname === '/') {
    // don't replace "www.site.com/blog" to "www.site.com/blog/blog" when blog is on homepage
    return;
  }

  if (get(document, 'referrer', '').substr(-1) === '/' && location.pathname.substr(-1) !== '/') {
    location.pathname = `${location.pathname}/`;
  }

  // avoids unintended location pushes such as:
  // land in '/create-post', on redirect to '/', pushes '/' then '/create-post' and then '/'
  const newSection = getWixSectionPathname(location);
  if (trimStart('/', location.pathname) !== trimStart('/', newSection)) {
    replaceSectionState(state, getWixSectionPathname(location));
  }
};

export default function listenToHistoryStateChange(store, history) {
  let lastState = null;
  syncWixSectionState(store.getState());

  const hasChanged = newState => lastState !== newState;

  setQueryParams(omit(history.getCurrentLocation().query || {}, ROUTING_PARAMS));

  history.listen(location => {
    const pathname = getWixSectionPathname(location);
    if (hasChanged(pathname) && !isErrorPage(pathname)) {
      lastState = pathname;

      if (isInSite()) {
        replaceSectionState(store.getState(), pathname);
      }
    }
  });

  const navigate = isOneApp(store.getState()) ? push : replace;
  Wix.addEventListener(Wix.Events.STATE_CHANGED, ({ newState }) => {
    if (newState === '') {
      newState = ROOT_PATH;
    }

    if (isInEditor() || !hasChanged(newState)) {
      return;
    }
    lastState = newState;

    const pathname = `/${newState.replace(/^\.\//, '')}`;
    store.dispatch(navigate(pathname));
  });

  Wix.addEventListener(Wix.Events.EDIT_MODE_CHANGE, () => {
    lastState = null;
  });
}
